/**
 * Mostly utilities for performing immutable updates of nested data types.
 * These should be used sparingly and usually indicate the data should be structured to be
 * less nested.
 * @module
 */
import type { RWTable, Table } from "common/data/table";
import arrayRemove from "common/utils/arrayRemove";
import arraySet from "common/utils/arraySet";
import { isObject } from "common/utils/isObject";

/**
 * Update a field in an object. No object is updated in place but instead
 * everything is copied on update.
 *
 * @param target The object to update (not mutated)
 * @param path The key path to the particular field to update
 * @param value The value to replace at that position
 * @returns A new object with the field inserted and all objects on the key path updated and replaced
 */
export function setIn<T>(target: T, path: [keyof T, ...(string | number)[]], value: any): T {
  if (path.length === 0) {
    return value;
  }

  if (target && target instanceof Array) {
    const idx = path[0] as number;
    return arraySet(target, idx, setIn(target[idx], path.slice(1) as any, value)) as any as T;
  }

  const key = path[0];

  const targetCopy = { ...target };
  targetCopy[key] = setIn(target[key] as any as T, path.slice(1) as any, value) as any;

  return targetCopy;
}

/**
 * Read a nested field value from an object using a path.
 *
 * @param target The object to read from
 * @param path The key path to the field
 * @returns The value at the path, or undefined if any segment is missing
 */
export function getIn(target: unknown, path: readonly (string | number)[]): unknown {
  let current = target;
  for (const segment of path) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return undefined;
    }
    current = (current as Record<string | number, unknown>)[segment];
  }
  return current;
}

/**
 * Apply a single shallow merge at a leaf, matching `mergeIn` empty-path semantics.
 */
function mergeLeaf(target: any, value: any): any {
  if (target && target instanceof Array && value instanceof Array) {
    return [...target, ...value];
  }

  // if string, do not destructure value into array of characters
  if (target && target instanceof Array && typeof value === "string") {
    return [...target, value];
  }

  return Object.assign({ ...target }, value);
}

/**
 * Merge multiple properties into an object immutably. No object is updated in place but instead
 * everything is copied on update.
 *
 * @param target The object to update (not mutated)
 * @param path The key path to the particular field to update
 * @param value The value to replace at that position
 * @returns A new object with the fields merged and all objects on the key path updated and replaced
 */
export function mergeIn<T extends {}>(target: T, path: [keyof T, ...(string | number)[]], value: any): T {
  if (path.length === 0) {
    return mergeLeaf(target, value);
  }

  if (target && target instanceof Array) {
    const idx = path[0] as number;
    return arraySet(target, idx, mergeIn(target[idx], path.slice(1) as any, value)) as any as T;
  }

  const key = path[0];

  const targetCopy = { ...target };
  targetCopy[key] = mergeIn(target[key] as any, path.slice(1) as any, value);
  return targetCopy;
}

function isMergeableNestedObject(value: unknown): value is Record<string, unknown> {
  if (!isObject(value) || ArrayBuffer.isView(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Shallow merge at the entity row replaces nested objects wholesale. When callers send a partial
 * nested object (only the field they changed or cleared), overlay those nested objects onto an
 * existing entity so unspecified nested fields are preserved. Incoming nested keys set to
 * `undefined` are removed, matching a per-entity clear-override.
 */
export function overlayNestedMergePropsOntoEntity<T>(entity: object | null | undefined, props: T): T {
  if (!entity || !isObject(props)) {
    return props;
  }

  const entityRecord = entity as Record<string, unknown>;
  const incomingProps = props as Record<string, unknown>;
  const overlaid: Record<string, unknown> = { ...incomingProps };

  for (const key of Object.keys(incomingProps)) {
    const incoming = incomingProps[key];
    const existing = entityRecord[key];
    if (!isMergeableNestedObject(incoming) || !isMergeableNestedObject(existing)) {
      continue;
    }

    const mergedNested: Record<string, unknown> = { ...existing, ...incoming };
    for (const nestedKey of Object.keys(incoming)) {
      if (incoming[nestedKey] === undefined) {
        delete mergedNested[nestedKey];
      }
    }
    overlaid[key] = mergedNested;
  }

  return overlaid as T;
}

/**
 * Delete a property from an object immutably. No object is updated in place but instead
 * everything is copied on update.
 *
 * @param target The object to update (not mutated)
 * @param path The key path to the particular field to update
 * @returns A new object with the fields merged and all objects on the key path updated and replaced
 */
export function deleteIn<T>(target: T, path: [keyof T, ...(string | number)[]]): T {
  if (!target) {
    return target;
  }

  if (path.length <= 1) {
    if (target && target instanceof Array) {
      const idx = path[0] as number;
      return arrayRemove(target, idx) as any as T;
    }

    const targetCopy = { ...target };
    delete targetCopy[path[0]];
    return targetCopy;
  }

  if (target && target instanceof Array) {
    const idx = path[0] as number;
    if (idx >= target.length) {
      return target;
    }
    return arraySet(target, idx, deleteIn(target[idx], path.slice(1) as any)) as any as T;
  }

  if (target[path[0]] === undefined) {
    return target;
  }

  const key = path[0];

  const targetCopy = { ...target };
  targetCopy[key] = deleteIn(target[key] as any, path.slice(1) as any);
  return targetCopy;
}

/**
 * Conditionally deletes a property from an object immutably. No object is updated in place but instead
 * everything is copied on update.
 *
 * @param target The object to update (not mutated)
 * @param path The key path to the particular field to update
 * @param pred The predicate function to determine if the value should be deleted
 * @returns A new object with the fields merged and all objects on the key path updated and replaced
 */
export function conditionalDeleteIn<T>(
  target: T,
  path: [keyof T, ...(string | number)[]],
  pred: (value: any) => boolean,
): T {
  if (!target) {
    return target;
  }

  if (path.length <= 1) {
    if (target && target instanceof Array) {
      const idx = path[0] as number;
      return pred(target[idx]) ? (arrayRemove(target, idx) as any as T) : target;
    }

    if (!pred(target[path[0]])) {
      return target;
    }

    const targetCopy = { ...target };
    delete targetCopy[path[0]];
    return targetCopy;
  }

  if (target && target instanceof Array) {
    const idx = path[0] as number;
    if (idx >= target.length) {
      return target;
    }
    return arraySet(target, idx, conditionalDeleteIn(target[idx], path.slice(1) as any, pred)) as any as T;
  }

  if (target[path[0]] === undefined) {
    return target;
  }

  const key = path[0];

  const targetCopy = { ...target };
  targetCopy[key] = conditionalDeleteIn(target[key] as any, path.slice(1) as any, pred);
  return targetCopy;
}

/**
 * Reorder a table using an ordered array of keys
 */
export function reorderTable<T>(
  originalTable: Table<T>,
  keys: readonly string[],
  updateSortIndex: boolean = false,
): Table<T> {
  const table: any = {};

  keys.forEach((key, i) => {
    table[key] = originalTable[key];

    if (updateSortIndex) {
      table[key] = { ...table[key], sortIndex: i };
    }
  });

  return table;
}

export interface HasId {
  readonly id: string;
}

export enum RelativePosition {
  BEFORE = "BEFORE",
  REPLACE = "REPLACE",
  AFTER = "AFTER",
}

/**
 * Inserts an item into a table relative to another item
 * @param table table to insert into
 * @param idRelativeTo item with id to insert relative to
 * @param relativity before, replace, or after
 * @param itemWithNewId item to insert with new id
 * @returns
 */
export function setInRelative<T extends HasId>(
  table: Table<T>,
  idRelativeTo: string,
  relativity: RelativePosition,
  itemWithNewId: T,
): Table<T> {
  const entries = Object.entries(table);
  const indexOfItem = entries.findIndex(([_, value]) => value.id === idRelativeTo);
  if (indexOfItem === -1) {
    return table;
  }
  const newEntry: [string, T] = [itemWithNewId.id, itemWithNewId];
  switch (relativity) {
    case RelativePosition.BEFORE:
      entries.splice(indexOfItem, 0, newEntry);
      break;
    case RelativePosition.REPLACE:
      entries.splice(indexOfItem, 1, newEntry);
      break;
    case RelativePosition.AFTER:
      entries.splice(indexOfItem + 1, 0, newEntry);
      break;
  }

  const newTable: RWTable<T> = {};
  for (const [key, value] of entries) {
    newTable[key] = value;
  }

  return newTable;
}

export function stringArrayToBooleanMap(array: string[], booleanVal: boolean): Record<string, boolean> {
  return Object.fromEntries(array.map((id) => [id, booleanVal]));
}

/**
 * Batch deletes multiple paths from an object immutably, in a single traversal reducing GC.
 * @param target The object to update (not mutated)
 * @param paths An array of key paths to delete
 * @returns A new object with all specified paths deleted
 */
export function batchDeleteIn<T>(target: T, paths: Path[]): T {
  const tree = buildDeleteTree(paths);
  return deleteTree(target, tree);
}

type Path = (string | number)[];

/**
 * Builds a tree from an array of paths.
 * Example: [['a','b'], ['a','c'], ['d']] => { a: { b: {}, c: {} }, d: {} }
 */
function buildDeleteTree(paths: Path[]): Record<string, any> {
  const tree: Record<string, any> = {};
  for (const path of paths) {
    let node = tree;
    for (const key of path) {
      if (!(key in node)) {
        node[key] = {};
      }
      node = node[key];
    }
  }
  return tree;
}

/**
 * Recursively deletes all keys in the deleteTree from the object.
 */
function deleteTree<T>(obj: T, deleteTreeObj: Record<string, any>): T {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  let changed = false;
  const result: any = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const key in deleteTreeObj) {
    const deleteTreeForKey = deleteTreeObj[key];
    if (Object.keys(deleteTreeForKey).length === 0) {
      // Delete this key
      if (key in result) {
        changed = true;
        if (Array.isArray(result)) {
          result.splice(Number(key), 1);
        } else {
          delete result[key];
        }
      }
    } else if (key in result) {
      // Recurse
      const child = deleteTree(result[key], deleteTreeForKey);
      if (child !== result[key]) {
        changed = true;
        result[key] = child;
      }
    }
  }
  return changed ? result : obj;
}

/**
 * Batch sets multiple paths in an object immutably, in a single traversal reducing GC.
 * @param target The object to update (not mutated)
 * @param pathValuePairs An array of [path, value] pairs to set
 * @returns A new object with all specified paths set
 */
export function batchSetIn<T>(target: T, pathValuePairs: [Path, any][]): T {
  const tree = buildSetTree(pathValuePairs);
  return setTree(target, tree);
}

/**
 * Internal merge-queue marker for {@link batchMergeIn}'s path tree.
 * A Symbol so it cannot collide with real object keys (unlike a `"__merges"` string).
 */
const BATCH_MERGE_VALUES: unique symbol = Symbol("batchMergeIn.values");

interface BatchMergeTreeNode {
  [BATCH_MERGE_VALUES]?: any[];
  [key: string]: BatchMergeTreeNode | undefined;
}

function createBatchMergeTreeNode(): BatchMergeTreeNode {
  return Object.create(null) as BatchMergeTreeNode;
}

/** JS property-key form of a path segment (`0` and `"0"` alias to the same key). */
function normalizePathSegmentKey(segment: string | number): string {
  return String(segment);
}

function normalizePathKeys(path: Path): string[] {
  return path.map(normalizePathSegmentKey);
}

/** Assign an own enumerable property without invoking legacy `__proto__` setters. */
function assignOwnProperty(obj: object, key: PropertyKey, value: unknown): void {
  Object.defineProperty(obj, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
}

/**
 * Like `mergeIn`, but materializes `{}` for null/undefined intermediates instead of throwing.
 * Used by {@link batchMergeIn}'s sequential fallback so null-root behavior matches the tree path.
 */
function mergeInMaterializing(target: any, path: Path, value: any): any {
  if (path.length === 0) {
    return mergeLeaf(target, value);
  }

  if (target === null || target === undefined || typeof target !== "object") {
    target = {};
  }

  if (Array.isArray(target)) {
    const idx = path[0] as number;
    return arraySet(target, idx, mergeInMaterializing(target[idx], path.slice(1), value));
  }

  const key = normalizePathSegmentKey(path[0]);
  const targetCopy = { ...target };
  assignOwnProperty(targetCopy, key, mergeInMaterializing(target[key], path.slice(1), value));
  return targetCopy;
}

/**
 * Batch merges multiple paths in an object immutably, in a single traversal reducing GC.
 *
 * Each pair uses the same leaf semantics as `mergeIn` (shallow object assign; array concat).
 * When no path is a prefix of another (pure sibling fan-out), merges share one tree walk.
 * When paths nest (ancestor/descendant), falls back to sequential merges so input order is
 * preserved — tree application would otherwise always run parent merges before children.
 *
 * Path segments are normalized to property keys (`0` and `"0"` are the same). Unlike `mergeIn`,
 * a null/undefined `target` materializes `{}` along the path rather than throwing. Prefer a real
 * object root in call sites.
 *
 * @param target The object to update (not mutated)
 * @param pathValuePairs An array of [path, value] pairs to merge
 * @returns A new object with all specified paths merged
 */
export function batchMergeIn<T>(target: T, pathValuePairs: [Path, any][]): T {
  if (pathValuePairs.length === 0) {
    return target;
  }

  if (pathValuePairsHaveAncestorRelationship(pathValuePairs)) {
    let result: any = target;
    for (const [path, value] of pathValuePairs) {
      result = mergeInMaterializing(result, path, value);
    }
    return result;
  }

  const tree = buildMergeTree(pathValuePairs);
  return applyMergeTree(target, tree);
}

/**
 * True when some path is a strict prefix of another (ancestor/descendant merge pair).
 * Segments are normalized to property keys first so `0` / `"0"` alias correctly.
 * O(n log n) in path count: lexicographic sort, then a linear adjacent scan.
 */
function pathValuePairsHaveAncestorRelationship(pathValuePairs: [Path, any][]): boolean {
  if (pathValuePairs.length < 2) {
    return false;
  }

  const paths = pathValuePairs.map(([path]) => normalizePathKeys(path));
  paths.sort(compareNormalizedPathsLexicographic);

  for (let i = 0; i < paths.length - 1; i++) {
    if (normalizedPathIsPrefix(paths[i], paths[i + 1])) {
      return true;
    }
  }
  return false;
}

function compareNormalizedPathsLexicographic(a: string[], b: string[]): number {
  const sharedLength = Math.min(a.length, b.length);
  for (let i = 0; i < sharedLength; i++) {
    if (a[i] < b[i]) {
      return -1;
    }
    if (a[i] > b[i]) {
      return 1;
    }
  }
  return a.length - b.length;
}

function normalizedPathIsPrefix(prefix: string[], path: string[]): boolean {
  if (prefix.length === 0) {
    return path.length > 0;
  }
  if (prefix.length >= path.length) {
    return false;
  }
  for (let i = 0; i < prefix.length; i++) {
    if (prefix[i] !== path[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Builds a tree from an array of path-value pairs for batched merges.
 * Multiple merges at the same path are queued in input order.
 * Nodes are null-prototype so path segments like `__proto__` / `constructor` cannot escape the trie.
 */
function buildMergeTree(pathValuePairs: [Path, any][]): BatchMergeTreeNode {
  const tree = createBatchMergeTreeNode();
  for (const [path, value] of pathValuePairs) {
    let node = tree;
    for (const segment of path) {
      const key = normalizePathSegmentKey(segment);
      if (!Object.hasOwn(node, key)) {
        node[key] = createBatchMergeTreeNode();
      }
      node = node[key] as BatchMergeTreeNode;
    }
    const merges = node[BATCH_MERGE_VALUES];
    if (merges === undefined) {
      node[BATCH_MERGE_VALUES] = [value];
    } else {
      merges.push(value);
    }
  }
  return tree;
}

/**
 * Recursively applies queued merges from the merge tree for pure sibling fan-out.
 * Same-path merges run in input order. Ancestor pairs are filtered out before this runs, so
 * `hasMerges && hasChildren` on one node is not expected; both flags are still handled.
 */
function applyMergeTree<T>(obj: T, mergeTreeObj: BatchMergeTreeNode): T {
  const mergeValues = mergeTreeObj[BATCH_MERGE_VALUES];
  const hasMerges = mergeValues !== undefined && mergeValues.length > 0;
  const childKeys = Object.keys(mergeTreeObj);
  const hasChildren = childKeys.length > 0;

  if (!hasMerges && !hasChildren) {
    return obj;
  }

  if (obj === null || obj === undefined || typeof obj !== "object") {
    if (!hasChildren && hasMerges) {
      let result: any = obj;
      for (const value of mergeValues) {
        result = mergeLeaf(result, value);
      }
      return result;
    }
    if (!hasChildren) {
      return obj;
    }
    // Missing intermediate objects become `{}`, matching mergeInMaterializing on null/undefined.
    obj = {} as T;
  }

  let result: any = obj;
  let changed = false;

  if (hasMerges) {
    for (const value of mergeValues) {
      result = mergeLeaf(result, value);
    }
    changed = true;
  }

  for (const key of childKeys) {
    const childTree = mergeTreeObj[key];
    if (childTree === undefined) {
      continue;
    }
    const child = applyMergeTree(result[key], childTree);
    if (child !== result[key]) {
      if (!changed) {
        result = Array.isArray(obj) ? [...(obj as any[])] : { ...(obj as object) };
      }
      assignOwnProperty(result, key, child);
      changed = true;
    }
  }

  return changed ? result : obj;
}

/**
 * Builds a tree from an array of path-value pairs.
 * Example: [[['a','b'], 1], [['a','c'], 2], [['d'], 3]] => { a: { b: { __value: 1 }, c: { __value: 2 } }, d: { __value: 3 } }
 */
function buildSetTree(pathValuePairs: [Path, any][]): Record<string, any> {
  const tree: Record<string, any> = {};
  for (const [path, value] of pathValuePairs) {
    let node = tree;
    for (const key of path) {
      if (!(key in node)) {
        node[key] = {};
      }
      node = node[key];
    }
    node.__value = value;
  }
  return tree;
}

/**
 * Recursively sets all values in the setTree to the object.
 */
function setTree<T>(obj: T, setTreeObj: Record<string, any>): T {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  let changed = false;
  const result: any = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const key in setTreeObj) {
    if (key === "__value") {
      continue; // Skip the special value marker
    }

    const setTreeForKey = setTreeObj[key];

    if ("__value" in setTreeForKey && Object.keys(setTreeForKey).length === 1) {
      // Set this key to the value
      const newValue = setTreeForKey.__value;
      if (result[key] !== newValue) {
        changed = true;
        result[key] = newValue;
      }
    } else {
      // Recurse
      const currentValue = result[key] || (Array.isArray(obj) ? [] : {});
      const child = setTree(currentValue, setTreeForKey);
      if (child !== result[key]) {
        changed = true;
        result[key] = child;
      }
    }
  }

  return changed ? result : obj;
}
